import { packageVersion } from '../version.js';
import { prepareCommandResult } from './agent-output.js';
import type { ParsedArgs } from './args.js';
import { hasFlag, parseCliArgs } from './args.js';
import { COMMAND_CATALOG } from './catalog.js';
import { adminCommand } from './commands/admin.js';
import { agentCommand } from './commands/agent.js';
import { authCommand } from './commands/auth.js';
import { backupCommand } from './commands/backup.js';
import { capabilitiesCommand } from './commands/capabilities.js';
import { catalogCommand } from './commands/catalog.js';
import { clientsCommand } from './commands/clients.js';
import { devicesCommand, stateCommand } from './commands/devices.js';
import { docsCommand } from './commands/docs.js';
import { doctorCommand } from './commands/doctor.js';
import { feedbackCommand } from './commands/feedback.js';
import { inboxCommand } from './commands/inbox.js';
import { inputCommand } from './commands/input.js';
import { logsCommand } from './commands/logs.js';
import { mappingsCommand } from './commands/mappings.js';
import { mediaCommand } from './commands/media.js';
import { onlineCommand } from './commands/online.js';
import { pairCommand } from './commands/pair.js';
import { playtimeCommand } from './commands/playtime.js';
import { profilesCommand } from './commands/profiles.js';
import { readersCommand } from './commands/readers.js';
import { rpcCommand } from './commands/rpc.js';
import { runCommand, stopCommand } from './commands/run.js';
import { screenshotCommand } from './commands/screenshot.js';
import { settingsCommand } from './commands/settings.js';
import { launchersCommand, systemsCommand } from './commands/systems.js';
import { tokensCommand } from './commands/tokens.js';
import { confirmCommand, uiCommand } from './commands/ui.js';
import { updateCommand } from './commands/update.js';
import { watchCommand } from './commands/watch.js';
import { CliError, classifyError, ExitCode } from './errors.js';
import type { CommandResult } from './output.js';
import { printResult } from './output.js';
import { enforceCommandPolicy } from './policy.js';

const PROGRAM_NAME = 'zaparoo-cli';
const PACKAGE_NAME = '@zaparoo/cli';

const HELP = `${PROGRAM_NAME} ${packageVersion}

Explore Zaparoo APIs, develop integrations, and diagnose live Core devices.

Usage:
  ${PROGRAM_NAME} [global options] <command> [args]

Global options:
  --device <host:port>       Target Core device
  --json                     Print machine-readable JSON
  --jsonl                    Print JSON Lines where supported
  --timeout <seconds>        Connect/request/watch timeout (default 30)
  --config <path>            Config file override
  --credentials-path <path>  Credential file override
  --trace                    Write redacted RPC trace JSONL
  --agent                    Compact JSON envelope; defaults policy to read-only
  --policy <mode>            read-only, interactive (default), or unrestricted
  --max-items <n>            Limit each returned array (agent default 50)
  --fields <a,b.c>           Select object fields in structured output
  --yes                      Confirm one state-changing command
  --version                  Print CLI version
  --help                     Print global or command help

Commands:
  catalog
  capabilities
  agent install|update|doctor
  docs list|search|get
  feedback
  devices list|scan|ping|default
  doctor
  pair status|begin|complete|cancel|forget|list
  rpc <method> [json-params]
  media status|search|browse|browse-index|active|active-update|history|history-latest|top|lookup
        meta|meta-update|image|tags|tags-update|title-parse|clean-orphans|control
        index status|start|cancel|resume
        scrapers
        scrape status|start|cancel|resume
  systems list
  launchers list|refresh
  run <zapscript-or-text>
  stop
  readers list|write|write-cancel
  tokens list|history
  mappings list|add|update|delete|reload
  settings get|update|reload
  clients list|current|delete|pair-begin|pair-cancel
  profiles list|new|update|delete|active|switch|verify
  ui state|respond
  confirm
  auth claim|status|unlink|link|link-status|link-cancel
  backup create|list|inspect|delete|restore|status|remote-run|remote-list|remote-restore
  playtime status|limits
  update check|apply
  admin health|update-check|update-apply|logs-download|auth-claim|playtime|playtime-limits
  inbox list|delete|clear
  input keyboard|gamepad
  screenshot
  state
  watch --seconds <n> --jsonl
  logs trace|download
  online auth|status|profile|sessions|cards|decks|devices|backups|request

Run '${PROGRAM_NAME} help <command>' for command details.
`;

const COMMAND_USAGE: Record<string, string> = {
  catalog: `${PROGRAM_NAME} catalog [--filter <text>] [--json]`,
  capabilities: `${PROGRAM_NAME} capabilities [--device <host:port>] [--json]`,
  agent: `${PROGRAM_NAME} agent install|update|doctor [options]`,
  docs: `${PROGRAM_NAME} docs list|search|get [options]`,
  feedback: `${PROGRAM_NAME} feedback [--category <bug|skill|feature|security>]`,
  devices: `${PROGRAM_NAME} devices list|scan|ping|default [options]`,
  doctor: `${PROGRAM_NAME} doctor [--device <host:port>] [--json]`,
  pair: `${PROGRAM_NAME} pair status|begin|complete|cancel|forget|list [options]`,
  rpc: `${PROGRAM_NAME} rpc <method> ['<json-params>'] [--json]`,
  media: `${PROGRAM_NAME} media status|search|browse|browse-index|active|active-update|history|history-latest|top|lookup
  ${PROGRAM_NAME} media meta|meta-update|image|tags|tags-update|title-parse|clean-orphans|control
  ${PROGRAM_NAME} media index status|start|cancel|resume [options]
  ${PROGRAM_NAME} media scrapers [options]
  ${PROGRAM_NAME} media scrape status|start|cancel|resume [options]`,
  systems: `${PROGRAM_NAME} systems list [--all]`,
  launchers: `${PROGRAM_NAME} launchers list|refresh`,
  run: `${PROGRAM_NAME} run <zapscript-or-text> [options]`,
  stop: `${PROGRAM_NAME} stop [--device <host:port>]`,
  readers: `${PROGRAM_NAME} readers list|write|write-cancel [options]`,
  tokens: `${PROGRAM_NAME} tokens list|history`,
  mappings: `${PROGRAM_NAME} mappings list|add|update|delete|reload [options]`,
  settings: `${PROGRAM_NAME} settings get|update|reload [options]`,
  clients: `${PROGRAM_NAME} clients list|current|delete|pair-begin|pair-cancel [options]`,
  profiles: `${PROGRAM_NAME} profiles list|new|update|delete|active|switch|verify [options]`,
  ui: `${PROGRAM_NAME} ui state | respond <id> --action <dismiss|select|confirm> [options]`,
  confirm: `${PROGRAM_NAME} confirm [--device <host:port>]`,
  auth: `${PROGRAM_NAME} auth claim|status|unlink|link|link-status|link-cancel [options]`,
  backup: `${PROGRAM_NAME} backup create|list|inspect|delete|restore|status|remote-run|remote-list|remote-restore`,
  playtime: `${PROGRAM_NAME} playtime status | limits get|update [options]`,
  update: `${PROGRAM_NAME} update check|apply`,
  admin: `${PROGRAM_NAME} admin <compatibility-action> [options]`,
  inbox: `${PROGRAM_NAME} inbox list|delete|clear [options]`,
  input: `${PROGRAM_NAME} input keyboard|gamepad <sequence> [options]`,
  screenshot: `${PROGRAM_NAME} screenshot [--output <path>] [--json]`,
  state: `${PROGRAM_NAME} state [--device <host:port>] [--json]`,
  watch: `${PROGRAM_NAME} watch [--seconds <n>] [--methods <a,b>] --jsonl`,
  logs: `${PROGRAM_NAME} logs trace [--last <n>] | download [--output <path>]`,
  online: `${PROGRAM_NAME} online auth|status|profile|sessions|cards|decks|devices|backups|request [options]`,
};

const COMMAND_SUMMARY: Record<string, string> = {
  catalog: 'List the machine-readable CLI command and safety contract.',
  capabilities: 'Inspect CLI coverage and safely probe Core compatibility.',
  agent: 'Install and verify bundled Agent Skills.',
  docs: 'Search bundled references and fixed authoritative public documentation.',
  feedback: 'Generate an official feedback link without sending private data.',
  devices: 'Discover Core devices and manage saved targets.',
  doctor: 'Run ordered connectivity, authentication, encryption, and API checks.',
  pair: 'Inspect and manage encrypted Core client pairing.',
  rpc: 'Call any Core JSON-RPC method as a forward-compatible debug escape hatch.',
  media: 'Inspect, search, browse, index, scrape, and control Core media.',
  systems: 'List indexed systems known to Core.',
  launchers: 'List or refresh launchers known to Core.',
  run: 'Send token text or ZapScript to Core.',
  stop: 'Stop active media when supported by its launcher.',
  readers: 'Inspect readers and manage NFC write operations.',
  tokens: 'Inspect active tokens and token launch history.',
  mappings: 'Inspect or mutate token mappings.',
  settings: 'Read, update, or reload Core settings.',
  clients: 'Inspect paired API clients and Core-side pairing state.',
  profiles: 'Inspect and manage Core device profiles.',
  ui: 'Inspect and respond to active Core UI events.',
  confirm: 'Confirm the currently staged launch-guard token.',
  auth: 'Inspect and manage Core device linking.',
  backup: 'Create, inspect, restore, and manage local or remote Core backups.',
  playtime: 'Inspect playtime state and limit configuration.',
  update: 'Check for or apply Core updates.',
  admin: 'Use compatibility aliases for older administrative command names.',
  inbox: 'Inspect or clear Core inbox messages.',
  input: 'Send keyboard or gamepad input through Core.',
  screenshot: 'Capture the current platform display to a local file.',
  state: 'Return a compact Core device-state snapshot.',
  watch: 'Stream a bounded set of Core notifications as JSON Lines.',
  logs: 'Inspect redacted local RPC traces or download the current Core log.',
  online: 'Query the public Zaparoo Online User API.',
};

function commandHelp(target: string[]): string {
  const requested = target.join(' ');
  const descendants = COMMAND_CATALOG.filter(
    (entry) => entry.path === requested || entry.path.startsWith(`${requested} `),
  );
  const exact = COMMAND_CATALOG.find((entry) => entry.path === requested);
  const invoked =
    exact ??
    [...COMMAND_CATALOG]
      .sort((a, b) => b.path.split(' ').length - a.path.split(' ').length)
      .find((entry) => entry.path.split(' ').every((segment, index) => target[index] === segment));
  const top = target[0];
  const summary = exact?.summary ?? COMMAND_SUMMARY[top];
  const usage = exact?.usage ?? COMMAND_USAGE[top];
  if ((!summary || !usage) && !invoked && descendants.length === 0) {
    throw new CliError(`Unknown command "${requested}"`, ExitCode.Usage);
  }
  if (exact) return commandDefinitionHelp(exact);
  if (invoked && descendants.length === 0) return commandDefinitionHelp(invoked);
  const lines = [
    `${PROGRAM_NAME} ${packageVersion}`,
    '',
    summary ?? `Commands under ${requested}.`,
    '',
    'Usage:',
    `  ${usage ?? descendants.map((entry) => entry.usage).join(`\n  `)}`,
  ];
  if (descendants.length > 0) {
    lines.push('', 'Concrete commands:');
    for (const entry of descendants) {
      lines.push(
        `  ${entry.usage.replace(`${PROGRAM_NAME} `, '')}`,
        `      ${entry.effect}; ${entry.summary}`,
      );
    }
  }
  return `${lines.join('\n')}\n`;
}

function commandDefinitionHelp(definition: (typeof COMMAND_CATALOG)[number]): string {
  return `${PROGRAM_NAME} ${packageVersion}\n\n${definition.summary}\n\nUsage:\n  ${definition.usage}\n\nPolicy:\n  effect=${definition.effect} confirmation=${definition.confirmation} source=${definition.source} trust=${definition.trust}\n${definition.capability ? `\nCapability:\n  ${definition.capability}\n` : ''}${definition.compatibility ? `\nCompatibility:\n  ${definition.compatibility}\n` : ''}`;
}

export async function run(input: string[] | ParsedArgs): Promise<CommandResult> {
  const args = Array.isArray(input) ? parseCliArgs(input) : input;
  if (hasFlag(args.flags, 'version')) {
    return {
      data: { name: PACKAGE_NAME, version: packageVersion, executable: PROGRAM_NAME },
      human: `${PROGRAM_NAME} ${packageVersion}`,
    };
  }

  const command = args.positionals[0];
  if (!command) return { data: { help: HELP }, human: HELP.trimEnd() };
  if (command === 'help') {
    const target = args.positionals.slice(1);
    if (target.length === 0) return { data: { help: HELP }, human: HELP.trimEnd() };
    const help = commandHelp(target);
    return { data: { help }, human: help.trimEnd() };
  }
  if (hasFlag(args.flags, 'help')) {
    const help = commandHelp(args.positionals);
    return { data: { help }, human: help.trimEnd() };
  }

  const definition = enforceCommandPolicy(args);
  const result = await dispatchCommand(command, args);
  return prepareCommandResult(result, args, definition);
}

async function dispatchCommand(command: string, args: ParsedArgs): Promise<CommandResult> {
  switch (command) {
    case 'catalog':
      return catalogCommand(args);
    case 'capabilities':
      return capabilitiesCommand(args);
    case 'agent':
      return agentCommand(args);
    case 'docs':
      return docsCommand(args);
    case 'feedback':
      return feedbackCommand(args);
    case 'devices':
      return devicesCommand(args);
    case 'doctor':
      return doctorCommand(args);
    case 'pair':
      return pairCommand(args);
    case 'rpc':
      return rpcCommand(args);
    case 'media':
      return mediaCommand(args);
    case 'systems':
      return systemsCommand(args);
    case 'launchers':
      return launchersCommand(args);
    case 'run':
      return runCommand(args);
    case 'stop':
      return stopCommand(args);
    case 'readers':
      return readersCommand(args);
    case 'tokens':
      return tokensCommand(args);
    case 'mappings':
      return mappingsCommand(args);
    case 'settings':
      return settingsCommand(args);
    case 'clients':
      return clientsCommand(args);
    case 'profiles':
      return profilesCommand(args);
    case 'ui':
      return uiCommand(args);
    case 'confirm':
      return confirmCommand(args);
    case 'auth':
      return authCommand(args);
    case 'backup':
      return backupCommand(args);
    case 'playtime':
      return playtimeCommand(args);
    case 'update':
      return updateCommand(args);
    case 'admin':
      return adminCommand(args);
    case 'input':
      return inputCommand(args);
    case 'inbox':
      return inboxCommand(args);
    case 'screenshot':
      return screenshotCommand(args);
    case 'state':
      return stateCommand(args);
    case 'watch':
      return watchCommand(args);
    case 'logs':
      return logsCommand(args);
    case 'online':
      return onlineCommand(args);
    default:
      throw new CliError(`Unknown command "${command}"`, ExitCode.Usage);
  }
}

export async function main(argv = process.argv.slice(2)): Promise<void> {
  let parsed: ReturnType<typeof parseCliArgs> | undefined;
  try {
    parsed = parseCliArgs(argv);
    const result = await run(parsed);
    if (!result.streamed && !(parsed.options.jsonl && parsed.positionals[0] === 'watch')) {
      printResult(result, parsed.options);
    }
    process.exitCode = result.exitCode ?? ExitCode.Success;
  } catch (error) {
    const cliError = classifyError(error);
    if (parsed?.options.json || argv.includes('--json')) {
      process.stderr.write(
        `${JSON.stringify({ error: cliError.message, code: cliError.code, data: cliError.data })}\n`,
      );
    } else {
      process.stderr.write(`Error: ${cliError.message}\n`);
    }
    process.exitCode = cliError.code;
  }
}
